import { HeadsetIcon } from 'lucide-react';
import { lazy, Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { toast } from 'sonner';

import { useCurrentUser } from '@/shared/auth';
import { ListSkeleton } from '@/shared/components/skeletons';
import { cn } from '@/shared/lib/cn';
import { useRealtimeEvent } from '@/shared/realtime';
import { CounterBadge } from '@/shared/ui/badge';
import { buttonVariants } from '@/shared/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/popover';
import { SimpleTooltip } from '@/shared/ui/tooltip';

import { useIsOperator, useWaitingCount } from '../hooks';
import { OpenlinesEvents } from '../realtime';
import type { SessionChangedPayload } from '../types';

const Waiting = lazy(() => import('./TopBarWaiting'));

/**
 * Top bar entry of open lines (operators only): dialogs waiting for the user, a popover with the queue and the user's
 * dialogs, the availability switch, and a toast when a dialog is assigned to the user.
 */
export function OpenlinesTopBarButton() {
  const operator = useIsOperator();
  return operator ? <WidgetButton /> : null;
}

function WidgetButton() {
  const { t } = useTranslation('openlines');
  const count = useWaitingCount() ?? 0;
  const [open, setOpen] = useState(false);
  const label = count > 0 ? t('widget.labelWaiting', { count }) : t('widget.label');
  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <SimpleTooltip content={t('widget.label')}>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label={label}
              data-testid="openlines-widget"
              data-waiting={count}
              className={cn(
                buttonVariants({ variant: 'ghost', size: 'icon' }),
                'relative text-muted-foreground',
              )}
            >
              <HeadsetIcon className="size-[1.125rem]" />
              <CounterBadge value={count} className="absolute -top-0.5 -right-0.5 ring-2 ring-surface" />
            </button>
          </PopoverTrigger>
        </SimpleTooltip>
        <PopoverContent align="end" className="w-[min(22rem,calc(100vw-1.5rem))] p-0">
          {open && (
            <Suspense fallback={<ListSkeleton rows={3} className="p-3" />}>
              <Waiting onNavigate={() => setOpen(false)} />
            </Suspense>
          )}
        </PopoverContent>
      </Popover>
      <AssignmentToasts />
    </>
  );
}

/** A toast when a dialog is assigned to the signed-in operator (outside the open lines page). */
function AssignmentToasts() {
  const { t } = useTranslation('openlines');
  const me = useCurrentUser();
  const navigate = useNavigate();
  const location = useLocation();
  const [seen] = useState(() => new Set<string>());
  useRealtimeEvent<SessionChangedPayload>(OpenlinesEvents.sessionChanged, (payload) => {
    if (payload.operatorId !== me.id || payload.status !== 'new' || seen.has(payload.sessionId)) return;
    seen.add(payload.sessionId);
    if (location.pathname.startsWith(`/messenger/openlines/${payload.sessionId}`)) return;
    toast.info(t('widget.assigned'), {
      action: {
        label: t('widget.open'),
        onClick: () => void navigate(`/messenger/openlines/${payload.sessionId}`),
      },
    });
  });
  return null;
}
